import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';

import {
  fixesFor,
  lessonFlags,
  lessonTexts,
  revertible,
  writeFor,
} from './language';

import type { FieldWrite, ReviewScene } from './edits';
import type { Applied, Flag } from './language';

const where = (flag: Flag): string => flag.text.where;

/**
 * American English, the B1 reading level and the coach voice, over the whole lesson —
 * LANG-01, SKILL-09.
 *
 * Spelling blocks publishing and the rest warns, which is the gate's split, so this panel
 * and the publish button agree. A fix is saved like any other edit, and stays revertible
 * for as long as this screen is open: the editor saves on blur, so a revert that lasted
 * only until the next save would vanish before anyone could press it.
 */
export function LanguageCheck({
  lessonId,
  lesson,
  disabled,
  onSave,
}: {
  lessonId: string;
  lesson: { title: string; summary: string; scenes: readonly ReviewScene[] };
  disabled: boolean;
  onSave: (write: FieldWrite) => void;
}) {
  const [applied, setApplied] = useState<Applied[]>([]);
  const [preview, setPreview] = useState<Set<string> | undefined>();

  const texts = useMemo(() => lessonTexts(lesson), [lesson]);
  const flags = useMemo(() => lessonFlags(texts), [texts]);
  const fixable = flags.filter((flag) => flag.issue.fix !== null);
  const { open, restore } = revertible(applied, texts);

  if (flags.length === 0 && open.length === 0) return null;

  const blocking = flags.filter((flag) => flag.issue.blocks).length;
  const warnings = flags.length - blocking;

  const apply = (label: string, chosen: readonly Flag[]) => {
    const fields = fixesFor(chosen);
    if (fields.length === 0) return;

    for (const field of fields)
      onSave(writeFor(lessonId, field.text, field.after));
    setApplied((was) => [
      ...was,
      { id: (was.at(-1)?.id ?? 0) + 1, label, fields },
    ]);
  };

  // A reverted fix leaves the list: a later fix writing the same words would otherwise
  // make it look revertible again, and undo a change that is already undone.
  const revert = (one: Applied) => {
    for (const field of one.fields)
      onSave(writeFor(lessonId, field.text, field.before));
    setApplied((was) => was.filter((other) => other.id !== one.id));
  };

  return (
    <Alert
      severity={blocking > 0 ? 'error' : flags.length > 0 ? 'warning' : 'info'}
      data-testid="language-check"
    >
      <AlertTitle>American English, reading level and coach voice</AlertTitle>
      <Stack spacing={1}>
        {flags.length > 0 && (
          <Typography variant="body2">
            {blocking > 0 &&
              `${String(blocking)} spelling ${blocking === 1 ? 'issue blocks' : 'issues block'} publishing. `}
            {warnings > 0 &&
              `${String(warnings)} ${warnings === 1 ? 'warning' : 'warnings'} you may publish over.`}
          </Typography>
        )}

        {flags.map((flag) => (
          <Stack
            key={flag.id}
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center' }}
            data-testid="language-flag"
          >
            <Chip
              size="small"
              color={flag.issue.blocks ? 'error' : 'warning'}
              label={flag.issue.blocks ? 'Blocks publishing' : 'Warning'}
            />
            <Typography variant="body2" sx={{ flex: 1 }}>
              <strong>{where(flag)}:</strong> {flag.issue.message}
            </Typography>
            {flag.issue.fix === null ? (
              <Typography variant="caption">Rewrite by hand</Typography>
            ) : (
              <Button
                size="small"
                disabled={disabled}
                onClick={() => {
                  apply(`${where(flag)}: "${flag.issue.found}"`, [flag]);
                }}
              >
                Apply fix
              </Button>
            )}
          </Stack>
        ))}

        {fixable.length > 1 && (
          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            sx={{ alignSelf: 'flex-start' }}
            onClick={() => {
              setPreview(new Set(fixable.map((flag) => flag.id)));
            }}
          >
            Apply all fixes
          </Button>
        )}

        {open.map((one) => (
          <Stack
            key={one.id}
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center' }}
            data-testid="language-applied"
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              Fixed {one.label}
            </Typography>
            <Button
              size="small"
              disabled={disabled}
              onClick={() => {
                revert(one);
              }}
            >
              Revert
            </Button>
          </Stack>
        ))}
        {open.length > 1 && (
          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            sx={{ alignSelf: 'flex-start' }}
            onClick={() => {
              for (const { text, to } of restore.values())
                onSave(writeFor(lessonId, text, to));
              setApplied([]);
            }}
          >
            Revert all
          </Button>
        )}
      </Stack>

      <Dialog
        open={preview !== undefined}
        onClose={() => {
          setPreview(undefined);
        }}
        maxWidth="md"
      >
        <DialogTitle>Apply these fixes?</DialogTitle>
        <DialogContent>
          <Stack>
            {fixable.map((flag) => (
              <FormControlLabel
                key={flag.id}
                control={
                  <Checkbox
                    checked={preview?.has(flag.id) ?? false}
                    onChange={(_event, checked) => {
                      setPreview((was) => {
                        const next = new Set(was);
                        if (checked) next.add(flag.id);
                        else next.delete(flag.id);
                        return next;
                      });
                    }}
                  />
                }
                label={`${where(flag)}: "${flag.issue.found}" becomes "${String(flag.issue.fix)}"`}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPreview(undefined);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={preview === undefined || preview.size === 0}
            onClick={() => {
              const chosen = fixable.filter((flag) => preview?.has(flag.id));
              apply(
                `${String(chosen.length)} ${chosen.length === 1 ? 'issue' : 'issues'} in one batch`,
                chosen,
              );
              setPreview(undefined);
            }}
          >
            Apply {preview?.size ?? 0} {preview?.size === 1 ? 'fix' : 'fixes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Alert>
  );
}
